import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import {
  ExclamationTriangleIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

const DeleteBrandConfirmation = ({ isOpen, onClose, brand, onBrandDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [canDelete, setCanDelete] = useState(true);

  useEffect(() => {
    if (isOpen && brand) {
      validateDeletion();
    }
  }, [isOpen, brand]);

  const validateDeletion = async () => {
    setValidating(true);
    
    try {
      const response = await axios.get(`/api/brands/${brand.id}/deletion-check`);
      setDeletionInfo(response.data);
      setCanDelete(response.data.canDelete);
    } catch (error) {
      console.error('Error validating brand deletion:', error);
      setCanDelete(false);
      setDeletionInfo({
        canDelete: false,
        productCount: 0,
        warnings: ['שגיאה בבדיקת האפשרות למחיקה']
      });
    } finally {
      setValidating(false);
    }
  };

  const handleDelete = async () => {
    if (!brand || !canDelete) return;

    setLoading(true);

    try {
      await axios.delete(`/api/brands/${brand.id}`);
      toast.success(`הברנד "${brand.name}" נמחק בהצלחה`);
      onBrandDeleted(brand.id);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה במחיקת הברנד');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !validating) {
      onClose();
    }
  };

  if (!brand) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="אישור מחיקת ברנד"
      size="md"
      preventClose={loading || validating}
    >
      <div className="space-y-6" dir="rtl">
        {validating ? (
          // Validation Loading State
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">בודק אפשרות מחיקה...</p>
          </div>
        ) : (
          <>
            {/* Warning Header */}
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                canDelete ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <ExclamationTriangleIcon className={`h-6 w-6 ${
                  canDelete ? 'text-yellow-600' : 'text-red-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {canDelete ? 'אישור מחיקת ברנד' : 'לא ניתן למחוק ברנד'}
                </h3>
                <p className="text-sm text-gray-600">
                  {brand.name}
                </p>
              </div>
            </div>

            {/* Brand Impact Information */}
            {deletionInfo && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">השפעת המחיקה:</h4>
                
                <div className="flex items-center gap-2">
                  <ShoppingBagIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {deletionInfo.productCount || 0}
                    </p>
                    <p className="text-xs text-gray-500">מוצרים שייכים לברנד</p>
                  </div>
                </div>
              </div>
            )}

            {/* Warnings */}
            {deletionInfo?.warnings && deletionInfo.warnings.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">אזהרות:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {deletionInfo.warnings.map((warning, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full ml-2"></span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Cannot Delete Message */}
            {!canDelete && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">
                  <strong>לא ניתן למחוק את הברנד</strong> מכיוון שיש לו מוצרים קשורים.
                </p>
                <p className="text-sm text-red-600 mt-2">
                  <strong>אפשרויות חלופיות:</strong>
                </p>
                <ul className="text-sm text-red-600 mt-1 space-y-1">
                  <li>• הסר את הברנד ממוצרים אלו תחילה</li>
                  <li>• שנה את סטטוס הברנד ל"לא פעיל"</li>
                  <li>• החזר את המוצרים לברנד אחר</li>
                </ul>
              </div>
            )}

            {/* Confirmation Message */}
            {canDelete && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>שים לב:</strong> מחיקת הברנד היא פעולה בלתי הפיכה. 
                  המוצרים שקשורים לברנד לא יימחקו, אך הקישור לברנד יוסר.
                </p>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        {!validating && (
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              ביטול
            </button>
            
            {canDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                )}
                <span>מחק ברנד</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                הבנתי
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DeleteBrandConfirmation;
